/**
 * Utilities for minor functions
 */

import axios from "axios";
import { AuthHeader, url } from "./_exports";

export const getDuration = (date: any) => {
	const now = new Date();
	const dateObj = new Date(date);
	const diffTime = Math.abs(now.getTime() - dateObj.getTime());

	// if less than 1 hour, return minutes ago
	if (diffTime < 1000 * 60 * 60) {
		return `${Math.floor(diffTime / (1000 * 60))} minute${
			Math.floor(diffTime / (1000 * 60)) > 1 ? "s" : ""
		} ago`;
	} else if (diffTime < 1000 * 60 * 60 * 24) {
		return `${Math.floor(diffTime / (1000 * 60 * 60))} hour${
			Math.floor(diffTime / (1000 * 60 * 60)) > 1 ? "s" : ""
		} ago`;
	} else if (diffTime > 1000 * 60 * 60 * 24 * 2) {
		return dateObj.toLocaleString();
	} else {
		return `${Math.floor(diffTime / (1000 * 60 * 60 * 24))} day${
			Math.floor(diffTime / (1000 * 60 * 60 * 24)) > 1 ? "s" : ""
		} ago`;
	}
};

export const clearCookie = () => {
	axios
		.get(`${url}/clearCookies`, { withCredentials: true })
		.then((res: any) => {})
		.catch(() => {});
};

export const refreshTenantRoutes = () => {
	return new Promise((resolve: any, reject: any) => {
		axios
			.get(`${url}/refresh-routes`, { ...AuthHeader })
			.then((res: any) => {
				resolve(res.data);
			})
			.catch((error: any) => {
				reject(error);
			});
	});
};

export const logout = (autoLogout: boolean = false) => {
	const LogoutURL: any = url + "/logout";
	clearCookie();
	localStorage.removeItem("accessToken");
	localStorage.removeItem("idToken");
	localStorage.removeItem("selectedFilters");
	if (autoLogout) {
		window.location.href = `/`;
	} else {
		window.location.href = `https://login.microsoftonline.com/common/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURI(
			LogoutURL
		)}`;
	}
};

export const convertToDMS = (value: any, type: String) => {
	let sign = Math.sign(value);
	value = Math.abs(value); // To remove the negative sign
	let degrees = Math.floor(value);
	let minutes = Math.floor((value - degrees) * 60);
	// I make the decimal to 4 decimal places to make it more accurate
	let seconds = ((value - degrees - minutes / 60) * 3600).toFixed(4);
	let dmsValue = `${degrees}° ${minutes}' ${seconds}"`;
	// Determine the direction based on the type and apply the sign
	let direction =
		type === "longitude" ? (sign === -1 ? "W" : "E") : sign === -1 ? "S" : "N";
	dmsValue = `${dmsValue} ${direction}`;
	return dmsValue;
};

export const checkSuperAdminPriviledge = (userRoles: any) => {
	if (userRoles?.includes(import.meta.env.VITE_SUPER_ADMIN_TAG)) {
		return true;
	} else {
		return false;
	}
};

export const checkRoles = (userRoles: string[], allowedRoles: string[]) => {
	if (userRoles?.includes(import.meta.env.VITE_SUPER_ADMIN_TAG)) {
		return true;
	} else {
		return allowedRoles.some((role: any) => {
			if (userRoles.includes(role)) {
				return true;
			}
		});
	}
};

export const checkPrivilege = (
	userRoles: any,
	requiredRoles: Array<string> | null | undefined
) => {
	if (userRoles?.includes(import.meta.env.VITE_SUPER_ADMIN_TAG)) {
		return true;
	} else if (requiredRoles?.length === 0 || !requiredRoles) {
		return true;
	} else {
		return requiredRoles?.some((element: any) => {
			if (userRoles?.includes(element)) {
				return true;
			}
		});
	}
};

export const checkProjectTeam = (userEmail: any, roleAssignments: any) => {
	const responsibleParty = Array.from(
		new Set(roleAssignments.flatMap((role: any) => role.appointee))
	);
	console.log(userEmail, responsibleParty);
	if (responsibleParty?.length === 0 || !responsibleParty) {
		return false;
	} else {
		return responsibleParty?.some((element: any) => {
			if (userEmail.toLowerCase() === element.toLowerCase()) {
				return true;
			}
		});
	}
};

// returns true if same and false if not
export const deepCompare = (prevState: any, currState: any) => {
	// Check if both objects are the same type
	if (typeof prevState !== typeof currState) {
		return false;
	}

	// Handle simple data types and null
	if (prevState === currState) {
		return true;
	}

	// Check for null or undefined
	if (
		prevState === null ||
		currState === null ||
		prevState === undefined ||
		currState === undefined
	) {
		return false;
	}

	// Check if both are arrays
	if (Array.isArray(prevState) && Array.isArray(currState)) {
		if (prevState.length !== currState.length) {
			return false;
		}
		for (let i = 0; i < prevState.length; i++) {
			if (!deepCompare(prevState[i], currState[i])) {
				return false;
			}
		}
		return true;
	}

	// Check if both are objects
	if (typeof prevState === "object" && typeof currState === "object") {
		const keys1 = Object.keys(prevState);
		const keys2 = Object.keys(currState);

		if (keys1.length !== keys2.length) {
			return false;
		}

		for (let key of keys1) {
			if (!deepCompare(prevState[key], currState[key])) {
				return false;
			}
		}
		return true;
	}

	return false;
};

export const getAllNodes = (menus: any, sidemenu: boolean = false) => {
	const endNodes: any = [];
	const traverse = (mItem: any, parentRoute: string = "") => {
		if (!mItem.children || mItem.children.length === 0) {
			// Node has no children, it is an end node
			endNodes.push({ ...mItem, path: `${parentRoute}/${mItem?.route?.link}` });
		} else {
			// Node has children, recursively traverse each child
			mItem.children.forEach((child: any) => {
				traverse(child, `${parentRoute}/${mItem?.route?.link}`);
			});
		}

		// include side menu items
		if (sidemenu) {
			if (mItem?.sidebarmenu || mItem?.sidebarmenu?.length > 0) {
				mItem?.sidebarmenu?.forEach((menu: any) => {
					if (menu?.children || menu?.children?.length > 0) {
						menu?.children?.forEach((sideChild: any) => {
							traverse(sideChild, `/${mItem?.route?.link}`);
						});
					}
				});
			}
		}
	};

	menus.forEach((mItem: any) => {
		traverse(mItem);
	});
	return endNodes;
};

export const getInitials = (name: any, numberSplice: number) => {
	if (name) {
		// Split the name into words
		const words = name.split(" ");

		// Initialize an empty string to store the initials
		let initials = "";

		// Iterate through the words and get the first letter of each
		for (let i = 0; i < words.length && i < 2; i++) {
			if (words[i][0]) {
				initials += words[i][0].toUpperCase();
			}
		}

		// setInitialsColor(alphabeticalColors[initials[0]]);

		return numberSplice ? initials.slice(0, numberSplice) : initials;
	} else {
		return "";
	}
};

export const includesIgnoreCase = (array: any, value: any) => {
	return (
		// Ensure `array` is an array because `some()` method is built for arrays only
		Array.isArray(array) &&
		// Ensure `value` is a string because `toLowerCase()` method is built for strings only
		typeof value === "string" &&
		array.some(
			(item: any) =>
				typeof item === "string" && item.toLowerCase() === value.toLowerCase()
		)
	);
};

export const emailsEqual = (a: any, b: any) => {
	if (typeof a !== "string" || typeof b !== "string") return false;
	return a.toLowerCase() === b.toLowerCase();
};

export const alphabeticalColors: any = {
	A: { fontColor: "#432818", backgroundColor: "#FF9A8B" },
	B: { fontColor: "#4A4829", backgroundColor: "#EDE275" },
	C: { fontColor: "#2E4A28", backgroundColor: "#8FE275" },
	D: { fontColor: "#391846", backgroundColor: "#A68DFF" },
	E: { fontColor: "#2C2C2C", backgroundColor: "#D9D9D9" },
	F: { fontColor: "#6D2C7D", backgroundColor: "#FFB1E6" },
	G: { fontColor: "#4A6828", backgroundColor: "#D2FFA2" },
	H: { fontColor: "#2C6C6C", backgroundColor: "#8DE1E1" },
	I: { fontColor: "#D94E2C", backgroundColor: "#FFA47D" },
	J: { fontColor: "#BF4227", backgroundColor: "#FFA25B" },
	K: { fontColor: "#55383D", backgroundColor: "#FFD2D8" },
	L: { fontColor: "#4C4C4C", backgroundColor: "#F0F0F0" },
	M: { fontColor: "#2D2828", backgroundColor: "#FF8D8D" },
	N: { fontColor: "#6C3A2C", backgroundColor: "#FFA37D" },
	O: { fontColor: "#614A22", backgroundColor: "#FFCDA5" },
	P: { fontColor: "#2D536D", backgroundColor: "#A7D4FF" },
	Q: { fontColor: "#503A3E", backgroundColor: "#FFA5A5" },
	R: { fontColor: "#A53B2C", backgroundColor: "#FF8D75" },
	S: { fontColor: "#6D5E2C", backgroundColor: "#FFE2A5" },
	T: { fontColor: "#2C6D47", backgroundColor: "#8DE7B5" },
	U: { fontColor: "#573B2C", backgroundColor: "#FFA07D" },
	V: { fontColor: "#2C5F6D", backgroundColor: "#8DC7FF" },
	W: { fontColor: "#6D2C6B", backgroundColor: "#FFB3E1" },
	X: { fontColor: "#4C6D2C", backgroundColor: "#C7FFA2" },
	Y: { fontColor: "#562C6D", backgroundColor: "#FFB5E7" },
	Z: { fontColor: "#2C6D61", backgroundColor: "#8DE7D1" },
};

export const toUTCISOString = (localDate: any) => {
	// Parse the local date (YYYY/MM/DD)
	console.log("localDate: ", localDate);
	//if date includes "/" or "-" then split with "/" or "-"
	const [year, month, day] = localDate.includes("/")
		? localDate.split("/").map(Number)
		: localDate.split("-").map(Number);

	// Create a Date object in local time
	const localDateObj = new Date(year, month - 1, day);

	// Convert to UTC and set the time to 16:00:00
	localDateObj.setUTCHours(16, 0, 0, 0);

	// Format as ISO string with timezone offset
	return localDateObj.toISOString().replace("Z", "+00:00");
};

export const filterInitialForm = (
	formData: Record<string, any>,
	initialData: Record<string, any>
) => {
	return Object.keys(formData).reduce(
		(acc, key) => {
			if (initialData[key] !== formData[key]) {
				acc[key] = initialData[key];
			}
			return acc;
		},
		{} as Record<string, any>
	);
};

export const getProjectMembers = (project: any) => {
	const members = [
		...(project.general_members ?? []),
		...(project.project_manager ?? []),
		...(project.role_assignments.flatMap((role: any) => role.appointee) ?? []),
	];
	return Array.from(new Set([...members]));
};

export const convertToHoursAndMinutesString = (durationMin: number) => {
	return `${Math.floor(durationMin / 60)}h ${durationMin % 60}m`;
};
